// src/Components/Orders/DeletedOrders.js

import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import "./DeletedOrders.css";
import {
  FiTrash2,
  FiRefreshCw,
  FiRotateCcw,
  FiAlertTriangle,
  FiSearch,
  FiPackage,
  FiChevronRight,
  FiChevronLeft,
  FiX,
  FiCheck,
  FiCalendar,
  FiUser,
  FiArrowLeft,
  FiInfo,
} from "react-icons/fi";
import { BiExport } from "react-icons/bi";

const API_BASE = process.env.REACT_APP_EP || "http://localhost:5000";

const PAGE_SIZE = 10;

const formatINR = (value) => {
  return new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 2,
  }).format(value ?? 0);
};

const formatDateTime = (dateString) => {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return "-";
  }
};

const formatDateShort = (dateString) => {
  if (!dateString) return "-";
  try {
    return new Date(dateString).toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  } catch {
    return "-";
  }
};

// Toast Component
const DeletedOrdersToast = ({ message, type, onClose }) => {
  useEffect(() => {
    const timer = setTimeout(onClose, 4000);
    return () => clearTimeout(timer);
  }, [onClose]);

  return (
    <div className={`do-toast do-toast-${type}`}>
      <div className="do-toast-icon">
        {type === "success" ? <FiCheck /> : <FiX />}
      </div>
      <span className="do-toast-message">{message}</span>
      <button className="do-toast-close" onClick={onClose}>
        ×
      </button>
    </div>
  );
};

// Confirmation Modal
const DeletedOrdersConfirmModal = ({
  isOpen,
  title,
  message,
  confirmText,
  cancelText,
  onConfirm,
  onCancel,
  isDanger,
  isLoading,
}) => {
  if (!isOpen) return null;

  return (
    <div className="do-modal-overlay" onClick={onCancel}>
      <div className="do-modal" onClick={(e) => e.stopPropagation()}>
        <div className={`do-modal-icon ${isDanger ? "do-modal-icon-danger" : "do-modal-icon-warning"}`}>
          <FiAlertTriangle />
        </div>
        <h3 className="do-modal-title">{title}</h3>
        <p className="do-modal-message">{message}</p>
        <div className="do-modal-actions">
          <button 
            className="do-modal-btn do-modal-btn-cancel" 
            onClick={onCancel}
            disabled={isLoading}
          >
            {cancelText || "Cancel"}
          </button>
          <button
            className={`do-modal-btn do-modal-btn-confirm ${isDanger ? "do-modal-btn-danger" : ""}`}
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? "Please wait..." : confirmText || "Confirm"}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeletedOrders = () => {
  const navigate = useNavigate();
  
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [stats, setStats] = useState(null);
  const [toasts, setToasts] = useState([]);

  // Modal state
  const [confirmModal, setConfirmModal] = useState({
    isOpen: false,
    title: "",
    message: "",
    confirmText: "",
    isDanger: false,
    onConfirm: null,
  });

  // Debounce search
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setCurrentPage(1);
    }, 400);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  // Toast helpers
  const addToast = useCallback((message, type = "success") => {
    const id = Date.now();
    setToasts((prev) => [...prev, { id, message, type }]);
  }, []);

  const removeToast = useCallback((id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  }, []);

  // Fetch deleted orders
  const fetchDeletedOrders = useCallback(async () => {
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: currentPage,
        limit: PAGE_SIZE,
      });
      
      if (debouncedSearch) {
        params.append("search", debouncedSearch);
      }

      const response = await fetch(`${API_BASE}/api/orders/deleted?${params}`, {
        credentials: "include",
      });
      
      if (!response.ok) {
        throw new Error("Failed to fetch deleted orders");
      }
      
      const data = await response.json();

      if (data.success) {
        setOrders(data.data || []);
        setTotalCount(data.pagination?.total || 0);
        setTotalPages(data.pagination?.totalPages || 1);
      } else {
        throw new Error(data.message || "Failed to fetch deleted orders");
      }
    } catch (err) {
      setError(err.message || "Failed to fetch deleted orders");
      console.error("Fetch deleted orders error:", err);
    } finally {
      setLoading(false);
    }
  }, [currentPage, debouncedSearch]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_BASE}/api/orders/deleted/stats`, {
        credentials: "include",
      });
      
      if (response.ok) {
        const data = await response.json();
        if (data.success) {
          setStats(data.data);
        }
      }
    } catch (err) {
      console.error("Failed to fetch stats:", err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    fetchDeletedOrders();
    fetchStats();
  }, [fetchDeletedOrders, fetchStats]);

  // Close modal helper
  const closeModal = () => {
    setConfirmModal((prev) => ({ ...prev, isOpen: false }));
  };

  // Restore order
  const handleRestore = (orderId, orderNumber) => {
    setConfirmModal({
      isOpen: true,
      title: "Restore Order",
      message: `Are you sure you want to restore order ${orderNumber}? The order will be moved back to active orders and inventory will be adjusted.`,
      confirmText: "Restore Order",
      isDanger: false,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const response = await fetch(`${API_BASE}/api/orders/${orderId}/restore`, {
            method: "POST",
            credentials: "include",
          });
          
          const data = await response.json();

          if (response.ok && data.success) {
            addToast("Order restored successfully!", "success");
            fetchDeletedOrders();
            fetchStats();
          } else {
            addToast(data.message || "Failed to restore order", "error");
          }
        } catch (err) {
          console.error("Restore error:", err);
          addToast("Failed to restore order", "error");
        } finally {
          setActionLoading(false);
          closeModal();
        }
      },
    });
  };

  // Permanently delete order
  const handlePermanentDelete = (orderId, orderNumber) => {
    setConfirmModal({
      isOpen: true,
      title: "Permanently Delete Order",
      message: `Are you sure you want to permanently delete order ${orderNumber}? This action CANNOT be undone. All order data will be permanently removed.`,
      confirmText: "Delete Forever",
      isDanger: true,
      onConfirm: async () => {
        setActionLoading(true);
        try {
          const response = await fetch(`${API_BASE}/api/orders/${orderId}/permanent`, {
            method: "DELETE",
            credentials: "include",
          });
          
          const data = await response.json();

          if (response.ok && data.success) {
            addToast("Order permanently deleted", "success");
            fetchDeletedOrders();
            fetchStats();
          } else {
            addToast(data.message || "Failed to delete order", "error");
          }
        } catch (err) {
          console.error("Permanent delete error:", err);
          addToast("Failed to delete order", "error");
        } finally {
          setActionLoading(false);
          closeModal();
        }
      },
    });
  };

  // Export deleted orders
  const handleExport = () => {
    if (orders.length === 0) {
      addToast("No orders to export", "error");
      return;
    }

    const csvContent = [
      ["Order ID", "Customer", "Location", "Total Amount", "Created At", "Deleted At", "Deleted By", "Reason"],
      ...orders.map((o) => [
        `ORD-${o.orderId?.substring(0, 8).toUpperCase()}`,
        o.customerName || "Unknown",
        `${o.talukName || ""}, ${o.districtName || ""}`.replace(/^, |, $/g, "") || "-",
        o.totalAmount || 0,
        formatDateShort(o.createdAt),
        formatDateTime(o.deletedAt),
        o.deletedBy || "System",
        o.deletionReason || "-",
      ]),
    ]
      .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(","))
      .join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = `deleted-orders-${new Date().toISOString().split("T")[0]}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
    
    addToast("Export completed", "success");
  };

  // Go back to orders
  const goBack = () => {
    navigate("/orders");
  };

  // Pagination calculations
  const startItem = totalCount === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const endItem = Math.min(currentPage * PAGE_SIZE, totalCount);

  // Generate page numbers
  const pageNumbers = useMemo(() => {
    const pages = [];
    const maxVisible = 5;
    
    if (totalPages <= maxVisible) {
      for (let i = 1; i <= totalPages; i++) pages.push(i);
    } else {
      if (currentPage <= 3) {
        for (let i = 1; i <= 4; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push(1);
        pages.push("...");
        for (let i = totalPages - 3; i <= totalPages; i++) pages.push(i);
      } else {
        pages.push(1);
        pages.push("...");
        for (let i = currentPage - 1; i <= currentPage + 1; i++) pages.push(i);
        pages.push("...");
        pages.push(totalPages);
      }
    }
    
    return pages;
  }, [currentPage, totalPages]);

  return (
    <div className="do-page">
      {/* Toasts */}
      <div className="do-toast-container">
        {toasts.map((toast) => (
          <DeletedOrdersToast
            key={toast.id}
            message={toast.message}
            type={toast.type}
            onClose={() => removeToast(toast.id)}
          />
        ))}
      </div>

      {/* Confirmation Modal */}
      <DeletedOrdersConfirmModal
        isOpen={confirmModal.isOpen}
        title={confirmModal.title}
        message={confirmModal.message}
        confirmText={confirmModal.confirmText}
        isDanger={confirmModal.isDanger}
        onConfirm={confirmModal.onConfirm}
        onCancel={closeModal}
        isLoading={actionLoading}
      />

      {/* Header */}
      <div className="do-header">
        <div className="do-header-left">
          <button className="do-back-btn" onClick={goBack}>
            <FiArrowLeft />
          </button>
          <div className="do-header-content">
            <h1 className="do-title">
              <FiTrash2 className="do-title-icon" />
              Deleted Orders
            </h1>
            <div className="do-breadcrumbs">
              <span className="do-breadcrumb-link" onClick={() => navigate("/")}>
                Dashboard
              </span>
              <FiChevronRight className="do-breadcrumb-sep" />
              <span className="do-breadcrumb-link" onClick={() => navigate("/orders")}>
                Orders
              </span>
              <FiChevronRight className="do-breadcrumb-sep" />
              <span className="do-breadcrumb-current">Trash</span>
            </div>
          </div>
        </div>
        <div className="do-header-right">
          <button
            className="do-btn do-btn-icon"
            onClick={() => {
              fetchDeletedOrders();
              fetchStats();
            }}
            title="Refresh"
            disabled={loading}
          >
            <FiRefreshCw className={loading ? "do-spin" : ""} />
          </button>
          <button 
            className="do-btn do-btn-secondary" 
            onClick={handleExport}
            disabled={orders.length === 0}
          >
            <BiExport />
            <span>Export</span>
          </button>
        </div>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="do-stats-grid">
          <div className="do-stat-card">
            <div className="do-stat-icon do-stat-icon-deleted">
              <FiTrash2 />
            </div>
            <div className="do-stat-content">
              <span className="do-stat-value">{stats.deletedCount || 0}</span>
              <span className="do-stat-label">Deleted Orders</span>
            </div>
          </div>
          <div className="do-stat-card">
            <div className="do-stat-icon do-stat-icon-value">
              <span>₹</span>
            </div>
            <div className="do-stat-content">
              <span className="do-stat-value">{formatINR(stats.deletedTotalValue || 0)}</span>
              <span className="do-stat-label">Total Value Lost</span>
            </div>
          </div>
          <div className="do-stat-card">
            <div className="do-stat-icon do-stat-icon-active">
              <FiPackage />
            </div>
            <div className="do-stat-content">
              <span className="do-stat-value">{stats.activeCount || 0}</span>
              <span className="do-stat-label">Active Orders</span>
            </div>
          </div>
        </div>
      )}

      {/* Info Banner */}
      <div className="do-info-banner">
        <FiInfo className="do-info-icon" />
        <p>
          Deleted orders are kept for 30 days before automatic permanent deletion. 
          You can restore orders or permanently delete them anytime.
        </p>
      </div>

      {/* Search Bar */}
      <div className="do-search-wrapper">
        <FiSearch className="do-search-icon" />
        <input
          type="text"
          className="do-search-input"
          placeholder="Search by order ID, customer name, or location..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button 
            className="do-search-clear" 
            onClick={() => setSearchQuery("")}
          >
            <FiX />
          </button>
        )}
      </div>

      {/* Table */}
      <div className="do-table-card">
        <div className="do-table-wrapper">
          {loading && (
            <div className="do-loading-overlay">
              <div className="do-loading-spinner"></div>
            </div>
          )}

          <table className="do-table">
            <thead>
              <tr>
                <th className="do-th">Order ID</th>
                <th className="do-th">Customer</th>
                <th className="do-th">Total</th>
                <th className="do-th">Deleted At</th>
                <th className="do-th">Deleted By</th>
                <th className="do-th">Reason</th>
                <th className="do-th do-th-actions">Actions</th>
              </tr>
            </thead>
            <tbody>
              {!loading && orders.length === 0 ? (
                <tr>
                  <td colSpan={7} className="do-empty-cell">
                    <div className="do-empty-state">
                      <FiTrash2 className="do-empty-icon" />
                      <h3 className="do-empty-title">No deleted orders</h3>
                      <p className="do-empty-text">
                        {debouncedSearch 
                          ? "No orders match your search criteria" 
                          : "Orders you delete will appear here"}
                      </p>
                      <button className="do-btn do-btn-primary" onClick={goBack}>
                        <FiArrowLeft />
                        Back to Orders
                      </button>
                    </div>
                  </td>
                </tr>
              ) : (
                orders.map((order) => {
                  const orderNumber = `ORD-${order.orderId?.substring(0, 8).toUpperCase()}`;
                  
                  return (
                    <tr key={order.orderId} className="do-tr">
                      <td className="do-td">
                        <span className="do-order-id">{orderNumber}</span>
                      </td>
                      <td className="do-td">
                        <div className="do-customer-cell">
                          <span className="do-customer-name">
                            {order.customerName || "Unknown"}
                          </span>
                          {(order.talukName || order.districtName) && (
                            <span className="do-customer-location">
                              {[order.talukName, order.districtName].filter(Boolean).join(", ")}
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="do-td">
                        <span className="do-total">{formatINR(order.totalAmount)}</span>
                      </td>
                      <td className="do-td">
                        <div className="do-date-cell">
                          <FiCalendar className="do-date-icon" />
                          <span>{formatDateTime(order.deletedAt)}</span>
                        </div>
                      </td>
                      <td className="do-td">
                        <div className="do-user-cell">
                          <FiUser className="do-user-icon" />
                          <span>{order.deletedBy || "System"}</span>
                        </div>
                      </td>
                      <td className="do-td">
                        <span 
                          className="do-reason" 
                          title={order.deletionReason || "No reason provided"}
                        >
                          {order.deletionReason || "-"}
                        </span>
                      </td>
                      <td className="do-td do-td-actions">
                        <div className="do-action-buttons">
                          <button
                            className="do-action-btn do-action-restore"
                            onClick={() => handleRestore(order.orderId, orderNumber)}
                            title="Restore Order"
                            disabled={actionLoading}
                          >
                            <FiRotateCcw />
                          </button>
                          <button
                            className="do-action-btn do-action-delete"
                            onClick={() => handlePermanentDelete(order.orderId, orderNumber)}
                            title="Delete Permanently"
                            disabled={actionLoading}
                          >
                            <FiTrash2 />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalCount > 0 && (
          <div className="do-table-footer">
            <div className="do-pagination-info">
              Showing {startItem} - {endItem} of {totalCount} deleted order{totalCount !== 1 ? "s" : ""}
              {debouncedSearch && <span className="do-filter-tag"> (filtered)</span>}
            </div>
            
            {totalPages > 1 && (
              <div className="do-pagination">
                <button
                  className="do-page-btn"
                  onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                  disabled={currentPage === 1 || loading}
                >
                  <FiChevronLeft />
                </button>
                
                {pageNumbers.map((page, index) => (
                  page === "..." ? (
                    <span key={`ellipsis-${index}`} className="do-page-ellipsis">...</span>
                  ) : (
                    <button
                      key={page}
                      className={`do-page-btn do-page-num ${currentPage === page ? "do-page-active" : ""}`}
                      onClick={() => setCurrentPage(page)}
                      disabled={loading}
                    >
                      {page}
                    </button>
                  )
                ))}
                
                <button
                  className="do-page-btn"
                  onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
                  disabled={currentPage === totalPages || loading}
                >
                  <FiChevronRight />
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DeletedOrders;